import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { useAuth } from '../../../contexts/AuthContext';
import referralService from '../../../services/referralService';

const ReferralBadge = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [referralStats, setReferralStats] = useState({
    totalReferrals: 0,
    pendingReferrals: 0,
    completedReferrals: 0,
    totalRewards: 0,
    currentTier: 'Bronze',
    nextTier: 'Silver',
    pointsToNextTier: 3
  });

  const [referralCode, setReferralCode] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadReferralData();
    }
  }, [user]);

  const loadReferralData = async () => {
    setLoading(true);

    try {
      const [codeResult, statsResult] = await Promise.all([
        referralService.getUserReferralCode(user.id),
        referralService.getUserReferralStats(user.id)
      ]);

      if (codeResult.success && codeResult.data?.referralCode) {
        setReferralCode(codeResult.data.referralCode);
      } else {
        // Generate a temporary code based on user ID if referral system isn't set up
        setReferralCode(`INVITE${user.id.substring(0, 8).toUpperCase()}`);
      }

      if (statsResult.success && statsResult.data) {
        setReferralStats(statsResult.data);
      }
      // Keep default fallback stats if API call fails
    } catch (error) {
      console.error('Error loading referral data:', error);
      // Generate temporary code on error
      setReferralCode(`INVITE${user.id.substring(0, 8).toUpperCase()}`);
    }

    setLoading(false);
  };

  const handleCopyReferralCode = async () => {
    try {
      await navigator.clipboard.writeText(referralCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = referralCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleShareReferral = (platform) => {
    const message = `Join ShaadiNest using my referral code ${referralCode} and get $25 off your first vendor booking!`;
    
    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(message)}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(message)}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`);
        break;
      case 'email':
        window.open(`mailto:?subject=Join ShaadiNest&body=${encodeURIComponent(message)}`);
        break;
    }
    setShowShareModal(false);
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'Bronze': return 'from-amber-500 to-amber-600';
      case 'Silver': return 'from-gray-400 to-gray-500';
      case 'Gold': return 'from-yellow-400 to-yellow-500';
      case 'Platinum': return 'from-purple-400 to-purple-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getTierIcon = (tier) => {
    switch (tier) {
      case 'Bronze': return 'Award';
      case 'Silver': return 'Medal';
      case 'Gold': return 'Trophy';
      case 'Platinum': return 'Crown';
      default: return 'Award';
    }
  };

  const progressPercentage = Math.min((referralStats.totalRewards / (referralStats.totalRewards + referralStats.pointsToNextTier)) * 100, 100);

  return (
    <div className="bg-gradient-to-br from-primary-50 via-white to-secondary-50 rounded-xl shadow-soft p-6 border border-primary-100">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-heading font-semibold text-xl text-text-primary">
            Referral Program
          </h3>
          <p className="text-text-secondary text-sm">
            Invite friends and earn rewards
          </p>
        </div>
        <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getTierColor(referralStats.currentTier)} flex items-center justify-center shadow-soft`}>
          <Icon name={getTierIcon(referralStats.currentTier)} size={24} className="text-white" />
        </div>
      </div>

      {/* Current Tier & Progress */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center space-x-2">
            <span className="text-sm font-medium text-text-primary">Current Tier:</span>
            <span className={`px-3 py-1 rounded-full text-sm font-bold bg-gradient-to-r ${getTierColor(referralStats.currentTier)} text-white`}>
              {referralStats.currentTier}
            </span>
          </div>
          <div className="text-sm text-text-secondary">
            {referralStats.pointsToNextTier} points to {referralStats.nextTier}
          </div>
        </div>
        
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div 
            className="bg-gradient-to-r from-primary to-secondary h-3 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          ></div>
        </div>
      </div>

      {/* Referral Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="text-center">
          <div className="text-2xl font-bold text-primary">{referralStats.totalReferrals}</div>
          <div className="text-sm text-text-secondary">Total Referrals</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-500">{referralStats.pendingReferrals}</div>
          <div className="text-sm text-text-secondary">Pending</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-500">{referralStats.completedReferrals}</div>
          <div className="text-sm text-text-secondary">Completed</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-secondary">${referralStats.totalRewards}</div>
          <div className="text-sm text-text-secondary">Total Rewards</div>
        </div>
      </div>

      {/* Referral Code */}
      <div className="mb-6">
        <h4 className="font-medium text-text-primary mb-3">Your Referral Code</h4>
        <div className="flex items-center space-x-3">
          <div className="flex-1 bg-white border border-border rounded-lg p-3">
            <div className="flex items-center justify-between">
              <span className="font-data text-lg text-primary font-bold">{referralCode}</span>
              <Button
                variant="outline"
                size="sm"
                iconName={copied ? "Check" : "Copy"}
                onClick={handleCopyReferralCode}
              >
                {copied ? 'Copied!' : 'Copy'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Share Options */}
      <div className="mb-6">
        <h4 className="font-medium text-text-primary mb-3">Share & Earn</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { name: 'WhatsApp', icon: 'MessageCircle', color: 'bg-green-500' },
            { name: 'Facebook', icon: 'Facebook', color: 'bg-blue-600' },
            { name: 'Twitter', icon: 'Twitter', color: 'bg-blue-400' },
            { name: 'Email', icon: 'Mail', color: 'bg-gray-600' }
          ].map((platform) => (
            <button
              key={platform.name}
              onClick={() => handleShareReferral(platform.name.toLowerCase())}
              className={`${platform.color} text-white p-3 rounded-lg hover:opacity-90 transition-opacity duration-200 flex items-center justify-center space-x-2`}
            >
              <Icon name={platform.icon} size={16} />
              <span className="text-sm font-medium">{platform.name}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Call to Action */}
      <div className="bg-primary-100 rounded-lg p-4 text-center">
        <div className="mb-3">
          <Icon name="Gift" size={32} className="text-primary mx-auto mb-2" />
          <h4 className="font-semibold text-text-primary">Invite Friends, Get Rewards!</h4>
          <p className="text-sm text-text-secondary">
            Both you and your friend get $25 off when they make their first booking
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="primary"
            iconName="UserPlus"
            onClick={() => setShowShareModal(true)}
            className="flex-1"
          >
            Invite Friends Now
          </Button>
          <Button
            variant="outline"
            iconName="Eye"
            onClick={() => navigate('/referral-history')}
          >
            View History
          </Button>
        </div>
      </div>

      {/* Reward Tiers Info */}
      <div className="mt-6 pt-4 border-t border-border-light">
        <h4 className="font-medium text-text-primary mb-3">Reward Tiers</h4>
        <div className="space-y-2">
          {[
            { tier: 'Bronze', requirement: '0-2 referrals', reward: '$10 per referral' },
            { tier: 'Silver', requirement: '3-5 referrals', reward: '$15 per referral' },
            { tier: 'Gold', requirement: '6-10 referrals', reward: '$20 per referral' },
            { tier: 'Platinum', requirement: '11+ referrals', reward: '$25 per referral' }
          ].map((tier) => (
            <div key={tier.tier} className="flex items-center justify-between text-sm">
              <div className="flex items-center space-x-2">
                <Icon name={getTierIcon(tier.tier)} size={16} className="text-text-muted" />
                <span className={`font-medium ${referralStats.currentTier === tier.tier ? 'text-primary' : 'text-text-secondary'}`}>
                  {tier.tier}
                </span>
                <span className="text-text-muted">({tier.requirement})</span>
              </div>
              <span className="text-text-primary font-medium">{tier.reward}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-xl text-text-primary">
                Share Your Referral Code
              </h3>
              <button
                onClick={() => setShowShareModal(false)}
                className="text-text-muted hover:text-text-primary"
              >
                <Icon name="X" size={24} />
              </button>
            </div>

            <div className="mb-6">
              <p className="text-text-secondary mb-4">
                Share your code <span className="font-bold text-primary">{referralCode}</span> with friends and family!
              </p>

              <div className="grid grid-cols-2 gap-3">
                {[
                  { name: 'WhatsApp', icon: 'MessageCircle', color: 'bg-green-500' },
                  { name: 'Facebook', icon: 'Facebook', color: 'bg-blue-600' },
                  { name: 'Twitter', icon: 'Twitter', color: 'bg-blue-400' },
                  { name: 'Email', icon: 'Mail', color: 'bg-gray-600' }
                ].map((platform) => (
                  <button
                    key={platform.name}
                    onClick={() => handleShareReferral(platform.name.toLowerCase())}
                    className={`${platform.color} text-white p-4 rounded-lg hover:opacity-90 transition-opacity duration-200 flex items-center justify-center space-x-2`}
                  >
                    <Icon name={platform.icon} size={20} />
                    <span className="font-medium">{platform.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="bg-surface-100 rounded-lg p-4">
              <p className="text-sm text-text-secondary">
                💡 <strong>Tip:</strong> When your friends sign up using your code, both of you get $25 off!
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralBadge;